'use client';

import {
  isCompletedWorkProof,
  OrderProgressStageState,
  OrderWorkProofInspectionSummary,
} from 'bff';
import { format } from 'date-fns';
import { useTranslations } from 'i11n';
import Link from 'next/link';
import { cn, Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from 'ui';
import {
  ORDER_PROGRESS_OPEN_ATTEMPT_LABEL_KEY,
  ORDER_PROGRESS_STAGE_ICON,
  ORDER_PROGRESS_STAGE_LABEL_KEY,
  ORDER_PROGRESS_STATUS_MARK,
  ORDER_PROGRESS_USES_STAGE_ICON,
  OrderProgressAudience,
  orderProgressStageHref,
  orderProgressStageStatusLabelKey,
  orderProgressStageTone,
} from '../constants/order-progress';
import { actorName } from '../utils/actorName';

const formatDate = (date: string) => format(new Date(date), 'dd/MM/yyyy HH:mm');

// Names the active inspection only while the stage reports on it: once an older
// one completed the stage, the active one is a newer attempt (or an expired one).
const workProofDetail = (
  state: OrderProgressStageState,
  workProof: OrderWorkProofInspectionSummary | null,
  openAttemptLabel: string | null,
): string | null => {
  if (state.stage !== 'work_proof' || !workProof) return null;

  const reference = `#${workProof.external_id}`;

  if (openAttemptLabel) return `${openAttemptLabel} ${reference}`;

  const activeCompletedTheStage = isCompletedWorkProof(
    workProof.display_status,
  );

  return state.status !== 'done' || activeCompletedTheStage ? reference : null;
};

/**
 * A stepper node. The node is a real link to the tab where the stage is resolved
 * and the detail (who, when, why) opens on hover or on keyboard focus.
 */
export const OrderProgressStep = ({
  state,
  audience,
  basePath,
  isCurrent,
  workProof,
  scheduledFor,
}: {
  state: OrderProgressStageState;
  audience: OrderProgressAudience;
  basePath?: string;
  isCurrent: boolean;
  workProof: OrderWorkProofInspectionSummary | null;
  scheduledFor: string | null;
}) => {
  const t = useTranslations('orders.progress');

  const tone = orderProgressStageTone(state);
  const Icon = ORDER_PROGRESS_USES_STAGE_ICON[state.status]
    ? ORDER_PROGRESS_STAGE_ICON[state.stage]
    : ORDER_PROGRESS_STATUS_MARK[state.status];

  const stageLabel = t(ORDER_PROGRESS_STAGE_LABEL_KEY[state.stage]);
  const statusLabel = t(orderProgressStageStatusLabelKey(state, audience));
  const openAttemptLabel = state.open_attempt
    ? t(ORDER_PROGRESS_OPEN_ATTEMPT_LABEL_KEY[state.open_attempt][audience])
    : null;

  // Shown under the node and inside the tooltip: a single derivation so they
  // cannot disagree. Null on stages that did not happen yet.
  const formattedAt = state.at ? formatDate(state.at) : null;

  const details = [
    state.version ? t('version', { version: state.version }) : null,
    formattedAt,
    state.actor ? t('by', { actor: actorName(state.actor) }) : null,
    state.reason ? `«${state.reason}»` : null,
    workProofDetail(state, workProof, openAttemptLabel),
    // What matters here is WHEN THE WORK IS PLANNED, not when it was recorded
    // (`at` already says that).
    state.stage === 'scheduling' && scheduledFor
      ? t('scheduledFor', { at: formatDate(scheduledFor) })
      : null,
  ].filter((detail): detail is string => !!detail);

  const nodeClassName = cn(
    'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-shadow',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    tone.node,
    isCurrent && 'ring-2 ring-offset-2 ring-current',
  );

  const nodeContent = (
    <>
      <Icon
        className={cn(
          'h-4 w-4',
          state.status === 'in_progress' && 'motion-safe:animate-spin',
        )}
      />
      {/* The pending new attempt inside a completed stage, in the tone of the
          stages waiting on someone. Named by the sr-only text below. */}
      {openAttemptLabel && (
        <span
          aria-hidden
          className='absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-white bg-yellow-500'
        />
      )}
      {/* Third channel, on top of the glyph and the label: color never
          communicates the status on its own. It names the link too. */}
      <span className='sr-only'>
        {`${stageLabel}: ${statusLabel}${
          openAttemptLabel ? `, ${openAttemptLabel}` : ''
        }${formattedAt ? `, ${formattedAt}` : ''}`}
      </span>
    </>
  );

  return (
    <li
      className='flex flex-1 items-start gap-3 sm:flex-col sm:items-center sm:gap-1.5'
      aria-current={isCurrent ? 'step' : undefined}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Without a basePath there is nowhere to navigate: the node is a
              plain marker (tracking-only audiences). */}
          {basePath ? (
            <Link
              href={orderProgressStageHref(basePath, state.stage)}
              className={nodeClassName}
            >
              {nodeContent}
            </Link>
          ) : (
            <span className={nodeClassName}>{nodeContent}</span>
          )}
        </TooltipTrigger>

        {/* Portalled: the provider detail wrapper clips on the x axis, and the
            first and last nodes would lose half of their card. */}
        <TooltipPortal>
          <TooltipContent className='w-64 p-3' align='center' side='bottom'>
            <p className='font-semibold'>{stageLabel}</p>
            <p className={cn('text-xs font-medium', tone.label)}>
              {statusLabel}
            </p>
            {details.length > 0 && (
              <div className='mt-1.5 flex flex-col gap-0.5 text-xs text-muted-foreground'>
                {details.map((detail) => (
                  <span key={detail}>{detail}</span>
                ))}
              </div>
            )}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>

      <div className='flex min-w-0 flex-col text-left sm:items-center sm:text-center'>
        <span className='text-xs font-medium leading-tight'>{stageLabel}</span>
        <p
          aria-hidden
          className={cn(
            'text-[11px] leading-tight mb-0.5 font-medium',
            tone.label,
          )}
        >
          {statusLabel}
        </p>
        {formattedAt && (
          <span
            aria-hidden
            className='whitespace-nowrap text-[10px] leading-tight text-muted-foreground'
          >
            {formattedAt}
          </span>
        )}
      </div>
    </li>
  );
};
